import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useThemeMode } from "../../../../contexts/ThemeContext";
import AuthContext from "../../../../contexts/AuthContext";
import useProfile from "./hooks/useProfile";
import AvatarCropModal from "./components/AvatarCropModal";
import { ClubsService } from "@/core/http/services/ClubsService";
import { usePwaInstall } from "@/core/pwa";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import GetAppIcon from "@mui/icons-material/GetApp";
import LightModeIcon from "@mui/icons-material/LightMode";
import LogoutIcon from "@mui/icons-material/Logout";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import MenuItem from "@mui/material/MenuItem";

const Profile = () => {
  const { mode, toggleMode } = useThemeMode();
  const { canInstallApp, installApp } = usePwaInstall();
  const navigate = useNavigate();
  const { clubId: clubIdFromUrl } = useParams();
  const { userLoggedData, logout, isAuthLoading, setSelectedClubId } =
    useContext(AuthContext);
  const fileInputRef = useRef(null);
  const clubRulesFetchRef = useRef({ clubId: null, at: 0, inFlight: false });

  const {
    initialize,
    profileData,
    setProfileData,
    avatarUrl,
    loading,
    error,
    success,
    saveProfile,
    uploadAvatar,
    changePassword,
  } = useProfile();

  useEffect(() => {
    if (isAuthLoading) {
      return;
    }
    if (
      !userLoggedData ||
      !["USER", "PLAYER"].includes(userLoggedData?.role?.toUpperCase())
    ) {
      navigate("/login");
    }
  }, [userLoggedData, isAuthLoading, navigate]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const [cropModal, setCropModal] = useState({ open: false, imageSrc: "" });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    repeat: false,
  });
  const [regulamentoOpen, setRegulamentoOpen] = useState(false);

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    repeatPassword: "",
  });

  const handleProfileFieldChange = (field) => (event) => {
    setProfileData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handlePasswordFieldChange = (field) => (event) => {
    setPasswordData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSaveProfile = async () => {
    await saveProfile();
  };

  const handleSavePassword = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword) return;
    if (passwordData.newPassword !== passwordData.repeatPassword) return;

    const ok = await changePassword(
      passwordData.currentPassword,
      passwordData.newPassword,
    );
    if (ok) {
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        repeatPassword: "",
      });
    }
  };

  const handlePickAvatar = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCropModal({ open: true, imageSrc: reader.result });
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  const handleCropConfirm = async (file) => {
    await uploadAvatar(file);
    setCropModal({ open: false, imageSrc: "" });
  };

  const userError =
    error?.type === "profile" || error?.type === "avatar"
      ? error?.message
      : null;
  const passwordError = error?.type === "password" ? error?.message : null;

  const userSuccess =
    success?.type === "profile" || success?.type === "avatar"
      ? success?.message
      : null;
  const passwordSuccess =
    success?.type === "password" ? success?.message : null;

  const resolvedClubId =
    clubIdFromUrl || userLoggedData?.selectedClubId || null;
  const clubs = Array.isArray(userLoggedData?.clubs)
    ? userLoggedData.clubs
    : [];
  const selectedClub =
    clubs.find((c) => c?.id === resolvedClubId) || clubs[0] || null;

  const [clubRules, setClubRules] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchClubRules = async () => {
      if (!resolvedClubId) {
        setClubRules(null);
        return;
      }

      const now = Date.now();
      if (
        clubRulesFetchRef.current.inFlight ||
        (clubRulesFetchRef.current.clubId === resolvedClubId &&
          now - clubRulesFetchRef.current.at < 5000)
      ) {
        return;
      }

      clubRulesFetchRef.current = {
        clubId: resolvedClubId,
        at: now,
        inFlight: true,
      };

      try {
        const { status, data } = await ClubsService.getById(resolvedClubId);
        if (cancelled) return;
        if (status === 200 && data) {
          setClubRules({
            rulesPdfUrl: data.rulesPdfUrl ?? null,
            rulesImageUrl: data.rulesImageUrl ?? null,
          });
        }
      } finally {
        clubRulesFetchRef.current.inFlight = false;
      }
    };

    // If rules already exist in auth context, prefer them and skip fetch.
    const hasRulesInContext =
      Boolean(selectedClub?.rulesPdfUrl) ||
      Boolean(selectedClub?.rulesImageUrl);
    if (hasRulesInContext) {
      setClubRules({
        rulesPdfUrl: selectedClub?.rulesPdfUrl ?? null,
        rulesImageUrl: selectedClub?.rulesImageUrl ?? null,
      });
      return () => {
        cancelled = true;
      };
    }

    fetchClubRules();
    return () => {
      cancelled = true;
    };
  }, [resolvedClubId, selectedClub?.rulesImageUrl, selectedClub?.rulesPdfUrl]);

  const regulamentoImg =
    clubRules?.rulesImageUrl || selectedClub?.rulesImageUrl || null;
  const regulamentoPdf =
    clubRules?.rulesPdfUrl || selectedClub?.rulesPdfUrl || null;
  const hasRegulamentoImg = Boolean(regulamentoImg);
  const hasRegulamentoPdf = Boolean(regulamentoPdf);
  const hasRegulamento = hasRegulamentoImg || hasRegulamentoPdf;

  const handleClubChange = async (event) => {
    const nextClubId = event.target.value;
    await setSelectedClubId(nextClubId);
    if (clubIdFromUrl) {
      navigate(`/c/${nextClubId}/user/more`);
    }
  };

  return (
    <Stack spacing={2}>
      <Button
        onClick={toggleMode}
        variant="contained"
        startIcon={mode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
        sx={{
          borderRadius: 1,
          py: 1,
          fontWeight: 700,
          textTransform: "none",
        }}
      >
        Alterar Tema
      </Button>

      {canInstallApp ? (
        <Button
          onClick={installApp}
          variant="outlined"
          startIcon={<GetAppIcon />}
          sx={{
            borderRadius: 1,
            py: 1,
            fontWeight: 700,
            textTransform: "none",
          }}
        >
          Instalar aplicativo
        </Button>
      ) : null}

      {hasRegulamento ? (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Regulamento
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Consulte as regras do torneio.
          </Typography>
          <Stack direction="row" spacing={1}>
            {hasRegulamentoImg ? (
              <Button
                variant="contained"
                fullWidth
                startIcon={<ArticleOutlinedIcon />}
                onClick={() => setRegulamentoOpen(true)}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Ver Regulamento
              </Button>
            ) : null}
            {hasRegulamentoPdf ? (
              <Button
                variant="contained"
                fullWidth
                startIcon={<DownloadIcon />}
                component="a"
                href={regulamentoPdf}
                download="Regulamento.pdf"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Baixar PDF
              </Button>
            ) : null}
          </Stack>
        </Paper>
      ) : null}

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Usuário
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Alterar seus dados cadastrais.
        </Typography>

        {userError ? (
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
            {userError}
          </Typography>
        ) : null}

        {userSuccess ? (
          <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
            {userSuccess}
          </Typography>
        ) : null}

        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <Avatar sx={{ width: 56, height: 56 }} src={avatarUrl || undefined}>
            {profileData.name?.[0]?.toUpperCase() || "U"}
          </Avatar>
          <Button
            variant="contained"
            fullWidth
            startIcon={<EditIcon />}
            onClick={handlePickAvatar}
            disabled={loading.avatar}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {loading.avatar ? "Enviando..." : "Alterar foto"}
          </Button>
        </Stack>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleAvatarChange}
        />

        <Stack spacing={1.5}>
          <TextField
            label="Nome"
            size="small"
            value={profileData.name}
            onChange={handleProfileFieldChange("name")}
            fullWidth
          />

          <TextField
            label="Nascimento"
            size="small"
            type="date"
            value={profileData.birthDate || ""}
            onChange={handleProfileFieldChange("birthDate")}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Telefone"
            size="small"
            value={profileData.phone}
            onChange={handleProfileFieldChange("phone")}
            fullWidth
          />

          <TextField
            label="Email"
            size="small"
            value={profileData.email}
            onChange={handleProfileFieldChange("email")}
            fullWidth
            disabled
          />

          <Button
            variant="contained"
            onClick={handleSaveProfile}
            disabled={loading.profile}
            sx={{ mt: 0.5, textTransform: "none", fontWeight: 700 }}
          >
            {loading.profile ? "Salvando..." : "Salvar dados"}
          </Button>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Segurança
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Trocar sua senha de acesso.
        </Typography>

        {passwordError ? (
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
            {passwordError}
          </Typography>
        ) : null}

        {passwordSuccess ? (
          <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
            {passwordSuccess}
          </Typography>
        ) : null}

        <Stack spacing={1.5}>
          <TextField
            label="Senha atual"
            size="small"
            type={showPasswords.current ? "text" : "password"}
            value={passwordData.currentPassword}
            onChange={handlePasswordFieldChange("currentPassword")}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() =>
                      setShowPasswords((p) => ({ ...p, current: !p.current }))
                    }
                  >
                    {showPasswords.current ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="Nova senha"
            size="small"
            type={showPasswords.new ? "text" : "password"}
            value={passwordData.newPassword}
            onChange={handlePasswordFieldChange("newPassword")}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() =>
                      setShowPasswords((p) => ({ ...p, new: !p.new }))
                    }
                  >
                    {showPasswords.new ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {passwordData.newPassword ? (
            <Stack spacing={0.4} sx={{ pl: 0.5 }}>
              {[
                {
                  label: "Mais de 6 caracteres",
                  ok: passwordData.newPassword.length > 6,
                },
                {
                  label: "Pelo menos uma letra minúscula",
                  ok: /[a-z]/.test(passwordData.newPassword),
                },
                {
                  label: "Pelo menos uma letra maiúscula",
                  ok: /[A-Z]/.test(passwordData.newPassword),
                },
                {
                  label: "Pelo menos um número",
                  ok: /\d/.test(passwordData.newPassword),
                },
              ].map(({ label, ok }) => (
                <Stack
                  key={label}
                  direction="row"
                  spacing={0.6}
                  alignItems="center"
                >
                  {ok ? (
                    <CheckCircleOutlineIcon
                      sx={{ fontSize: 14, color: "success.main" }}
                    />
                  ) : (
                    <RadioButtonUncheckedIcon
                      sx={{ fontSize: 14, color: "text.disabled" }}
                    />
                  )}
                  <Typography
                    variant="caption"
                    color={ok ? "success.main" : "text.disabled"}
                  >
                    {label}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          ) : null}

          <TextField
            label="Repita sua senha"
            size="small"
            type={showPasswords.repeat ? "text" : "password"}
            value={passwordData.repeatPassword}
            onChange={handlePasswordFieldChange("repeatPassword")}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() =>
                      setShowPasswords((p) => ({ ...p, repeat: !p.repeat }))
                    }
                  >
                    {showPasswords.repeat ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box>
            <Button
              variant="contained"
              onClick={handleSavePassword}
              disabled={
                loading.password ||
                !passwordData.currentPassword ||
                !passwordData.newPassword ||
                passwordData.newPassword.length <= 6 ||
                !/[a-z]/.test(passwordData.newPassword) ||
                !/[A-Z]/.test(passwordData.newPassword) ||
                !/\d/.test(passwordData.newPassword) ||
                passwordData.newPassword !== passwordData.repeatPassword
              }
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {loading.password ? "Atualizando..." : "Atualizar senha"}
            </Button>
            {passwordData.newPassword &&
            passwordData.repeatPassword &&
            passwordData.newPassword !== passwordData.repeatPassword ? (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "block", mt: 0.5 }}
              >
                As senhas não conferem.
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </Paper>

      <Button
        onClick={logout}
        variant="contained"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 3,
          bgcolor: "primary.main",
          color: "primary.contrastText",
          "&:hover": { bgcolor: "primary.dark" },
          padding: "6px 25px",
        }}
      >
        {" "}
        <Typography variant="body2">Sair</Typography>
        <LogoutIcon />
      </Button>

      <AvatarCropModal
        open={cropModal.open}
        imageSrc={cropModal.imageSrc}
        onClose={() => setCropModal({ open: false, imageSrc: "" })}
        onConfirm={handleCropConfirm}
        loading={loading.avatar}
      />

      {hasRegulamentoImg ? (
        <Dialog
          open={regulamentoOpen}
          onClose={() => setRegulamentoOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
            Regulamento
            <IconButton
              onClick={() => setRegulamentoOpen(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 1 }}>
            <Box
              component="img"
              src={regulamentoImg}
              alt="Regulamento do torneio"
              sx={{ width: "100%", display: "block", borderRadius: 1 }}
            />
            <Button
              component="a"
              href={regulamentoImg}
              download="Regulamento.png"
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              fullWidth
              startIcon={<DownloadIcon />}
              sx={{ mt: 1, mb: 0.5, textTransform: "none", fontWeight: 700 }}
            >
              Baixar Imagem
            </Button>
          </DialogContent>
        </Dialog>
      ) : null}
    </Stack>
  );
};

export default Profile;
